/**
 * Cart Drawer — Gift Wrap & Shipping Protection
 *
 * Gift Wrap: Uses cart attribute (no product added to cart)
 *   – Checked by default (attribute absent or != 'no')
 *   – Unchecking sets attribute gift_wrap='no'
 *
 * Shipping Protection: Adds/removes a product variant from cart
 *   – Auto-added when regular products exist and user hasn't opted out
 *   – Auto-removed when no regular products remain
 */
(function () {

    var SHIPPING_PROTECTION_DISABLED_KEY = 'thekiss_shipping_protection_disabled';

    // Flag to prevent recursive auto-add during our own operations
    var isAutoAdding = false;

    /**
     * Prepare bundled sections for cart operations so cart-drawer HTML updates automatically
     */
    function prepareBundledSections() {
        var sectionsToBundle = [];

        document.dispatchEvent(
            new CustomEvent('cart:prepare-bundled-sections', {
                bubbles: true,
                detail: { sections: sectionsToBundle }
            })
        );

        if (sectionsToBundle.length === 0) {
            var cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
                var section = cartDrawer.closest('.shopify-section');
                if (section && section.id) {
                    sectionsToBundle.push(section.id.replace('shopify-section-', ''));
                }
            }
        }

        return sectionsToBundle;
    }

    // ─── Gift Wrap (cart attribute only, no page reload) ─────────────────────

    /**
     * Handle gift wrap checkbox — silently sets cart attribute gift_wrap=yes/no
     */
    async function handleGiftWrapToggle(checkbox) {
        checkbox.disabled = true;
        var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        try {
            var res = await fetch(rootUrl + 'cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ attributes: { gift_wrap: checkbox.checked ? 'yes' : 'no' } })
            });

            if (!res.ok) throw new Error('Gift wrap update failed: ' + res.status);
        } catch (e) {
            console.error('[Gift Wrap] Error:', e);
            checkbox.checked = !checkbox.checked;
        } finally {
            checkbox.disabled = false;
        }
    }

    // ─── Shipping Protection (cart product) ──────────────────────────────────

    /**
     * Add a single unit of a variant to cart
     */
    async function addToCart(variantId) {
        var sectionsToBundle = prepareBundledSections();
        var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        var res = await fetch(rootUrl + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
                items: [{ id: parseInt(variantId, 10), quantity: 1 }],
                sections: sectionsToBundle.join(',')
            })
        });

        if (!res.ok) {
            var text = await res.text();
            throw new Error('Add failed (' + res.status + '): ' + text);
        }

        return res.json();
    }

    /**
     * Remove a variant from cart by key
     */
    async function removeFromCart(variantId) {
        var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        var cartRes = await fetch(rootUrl + 'cart.js', { headers: { 'Accept': 'application/json' } });
        var cart = await cartRes.json();

        var lineItem = cart.items.find(function (item) {
            return String(item.variant_id) === String(variantId);
        });

        if (!lineItem) {
            console.warn('[Shipping Protection] Line item not found for variant:', variantId);
            return cart;
        }

        var sectionsToBundle = prepareBundledSections();

        var res = await fetch(rootUrl + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
                id: lineItem.key,
                quantity: 0,
                sections: sectionsToBundle.join(',')
            })
        });

        if (!res.ok) {
            var text = await res.text();
            throw new Error('Remove failed (' + res.status + '): ' + text);
        }

        return res.json();
    }

    function isManuallyDisabled(storageKey) {
        try { return localStorage.getItem(storageKey) === 'true'; } catch (e) { return false; }
    }

    function setManuallyDisabled(storageKey, value) {
        try {
            if (value) { localStorage.setItem(storageKey, 'true'); }
            else { localStorage.removeItem(storageKey); }
        } catch (e) { /* ignore */ }
    }

    function isVariantInCart(cart, variantId) {
        if (!cart || !cart.items) return false;
        return cart.items.some(function (item) {
            return String(item.variant_id) === String(variantId);
        });
    }

    /**
     * Returns true if cart has any items that are not shipping protection
     */
    function hasRegularProducts(cart, shippingProtectionVariantId) {
        if (!cart || !cart.items || cart.items.length === 0) return false;
        return cart.items.some(function (item) {
            return !shippingProtectionVariantId ||
                String(item.variant_id) !== String(shippingProtectionVariantId);
        });
    }

    /**
     * Handle shipping protection checkbox toggle (adds/removes product from cart)
     */
    async function handleShippingProtectionToggle(checkbox, variantId) {
        if (!variantId) {
            console.warn('[Shipping Protection] No variant ID found');
            checkbox.checked = !checkbox.checked;
            return;
        }

        console.log('[Shipping Protection] Toggle, checked:', checkbox.checked, 'variantId:', variantId);
        checkbox.disabled = true;

        try {
            var cartResponse;

            if (checkbox.checked) {
                cartResponse = await addToCart(variantId);
                setManuallyDisabled(SHIPPING_PROTECTION_DISABLED_KEY, false);
            } else {
                cartResponse = await removeFromCart(variantId);
                setManuallyDisabled(SHIPPING_PROTECTION_DISABLED_KEY, true);
            }

            document.dispatchEvent(
                new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: {
                        baseEvent: checkbox.checked ? 'shipping-protection:add' : 'shipping-protection:remove',
                        cart: cartResponse
                    }
                })
            );
        } catch (e) {
            console.error('[Shipping Protection] Error:', e);
            checkbox.checked = !checkbox.checked;
        } finally {
            checkbox.disabled = false;
        }
    }

    /**
     * Auto-add/remove shipping protection based on cart contents
     */
    async function autoAddExtras(cart) {
        if (isAutoAdding) return;
        if (!cart || !cart.items || cart.items.length === 0) return;

        var spElement = document.querySelector('shipping-protection');
        if (!spElement) return;

        var spVariantId = spElement.dataset.variantId;
        var hasRegular = hasRegularProducts(cart, spVariantId);

        if (!hasRegular) {
            // No regular products — remove shipping protection if present
            if (spVariantId && isVariantInCart(cart, spVariantId)) {
                console.log('[Auto-remove] Removing shipping protection (no regular products)');
                isAutoAdding = true;
                try {
                    var removed = await removeFromCart(spVariantId);
                    document.dispatchEvent(new CustomEvent('cart:change', {
                        bubbles: true,
                        detail: { baseEvent: 'auto-remove-extras', cart: removed }
                    }));
                } catch (e) {
                    console.error('[Auto-remove] Error:', e);
                } finally {
                    isAutoAdding = false;
                }
            }
            return;
        }

        // Regular products exist — auto-add shipping protection if not present and not opted out
        var spInCart = isVariantInCart(cart, spVariantId);
        var spDisabled = isManuallyDisabled(SHIPPING_PROTECTION_DISABLED_KEY);

        if (spVariantId && !spInCart && !spDisabled) {
            console.log('[Auto-add] Adding shipping protection');
            isAutoAdding = true;
            try {
                var sectionsToBundle = prepareBundledSections();
                var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

                var res = await fetch(rootUrl + 'cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({
                        items: [{ id: parseInt(spVariantId, 10), quantity: 1 }],
                        sections: sectionsToBundle.join(',')
                    })
                });

                if (!res.ok) throw new Error('Auto-add failed: ' + res.status);

                var cartResponse = await res.json();
                document.dispatchEvent(new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: { baseEvent: 'auto-add-extras', cart: cartResponse }
                }));
            } catch (e) {
                console.error('[Auto-add] Error:', e);
            } finally {
                isAutoAdding = false;
            }
        }
    }

    // ─── Event listeners ─────────────────────────────────────────────────────

    /**
     * Single document-level change listener — survives DOM replacement by Section Rendering API
     */
    document.addEventListener('change', function (e) {
        var target = e.target;

        if (target.id === 'gift-wrap-checkbox') {
            handleGiftWrapToggle(target);
            return;
        }

        if (target.id === 'shipping-protection-checkbox') {
            var sp = target.closest('shipping-protection');
            handleShippingProtectionToggle(target, sp && sp.dataset.variantId);
            return;
        }
    });

    /**
     * Listen for cart:change to auto-add shipping protection when regular products are added
     */
    document.addEventListener('cart:change', function (event) {
        var baseEvent = event.detail && event.detail.baseEvent;
        var cart = event.detail && event.detail.cart;

        // Skip events we fired ourselves
        if (baseEvent === 'auto-add-extras' ||
            baseEvent === 'auto-remove-extras' ||
            baseEvent === 'gift-wrap:add' ||
            baseEvent === 'gift-wrap:remove' ||
            baseEvent === 'shipping-protection:add' ||
            baseEvent === 'shipping-protection:remove') {
            return;
        }

        console.log('[Auto-add] Cart changed, checking shipping protection. Base event:', baseEvent);

        // Small delay to ensure cart-drawer elements are rendered after Section Rendering API update
        setTimeout(function () { autoAddExtras(cart); }, 100);
    });

})();

// ── Checkout button loading state ─────────────────────────────────────────
(function () {
    document.addEventListener('click', function (e) {
        var btn = e.target.closest('.cart-drawer__checkout-btn');
        if (!btn || btn.getAttribute('aria-busy') === 'true') return;
        btn.setAttribute('aria-busy', 'true');
    });

    // Reset on back navigation (bfcache restore)
    window.addEventListener('pageshow', function (e) {
        if (e.persisted) {
            var btn = document.querySelector('.cart-drawer__checkout-btn');
            if (btn) btn.removeAttribute('aria-busy');
        }
    });

     // ── Line Item Loader ──────────────────────────────────────────────────────
    function ensureLoader(lineItem) {
        if (!lineItem.querySelector('.line-item__loader')) {
            lineItem.insertAdjacentHTML(
                'beforeend', 
                '<div class="line-item__loader"><div class="line-item__spinner"></div></div>'
            );
        }
    }

    document.addEventListener('click', function (e) {
        const cartDrawer = e.target.closest('#cart-drawer');
        if (!cartDrawer) return;

        const actionBtn = e.target.closest('a[href*="url_to_remove"], a[href*="/cart/change?"], quantity-selector a, quantity-selector button');
        
        if (actionBtn && !actionBtn.disabled) {
            const lineItem = actionBtn.closest('.line-item');
            if (lineItem) {
                ensureLoader(lineItem);
                lineItem.classList.add('is-loading');
            }
        }
    });

    document.addEventListener('change', function (e) {
        const cartDrawer = e.target.closest('#cart-drawer');
        if (!cartDrawer) return;

        const qtyInput = e.target.closest('quantity-selector input');
        if (qtyInput) {
            const lineItem = qtyInput.closest('.line-item');
            if (lineItem) {
                ensureLoader(lineItem);
                lineItem.classList.add('is-loading');
            }
        }
    });
})();

// ── Sticky Cart Checkout ──────────────────────────────────────────────────────
(function () {
    class CartStickyCheckout extends HTMLElement {
        connectedCallback() {
            this._observer = new IntersectionObserver((entries) => {
                this.classList.toggle('is-visible', !entries[0].isIntersecting);
            });

            const staticBtn = document.querySelector('[data-static-checkout]');
            if (staticBtn) {
                this._observer.observe(staticBtn);
            }
        }

        disconnectedCallback() {
            this._observer?.disconnect();
        }
    }

    if (!window.customElements.get('cart-sticky-checkout')) {
        window.customElements.define('cart-sticky-checkout', CartStickyCheckout);
    }
})();
